import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ClientModal from '@/components/ClientModal';

gsap.registerPlugin(ScrollTrigger);

// Client data type
interface Client {
  key: number;
  title: string;
  role: string;
  image: string;
  link: string;
  whatWeDid: string;
}

const Vectoral = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const nodesContainerRef = useRef<HTMLDivElement>(null);
  const [hoveredNode, setHoveredNode] = useState<number | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [radius, setRadius] = useState(280);

  // Load clients data
  useEffect(() => {
    fetch('/clients/clients.json')
      .then((res) => res.json())
      .then((data: Client[]) => {
        setClients(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load clients:', err);
        setLoading(false);
      });
  }, []);

  // Calculate node position based on angle
  const getNodePosition = (angle: number, radius: number) => {
    const radians = (angle * Math.PI) / 180;
    const x = Math.sin(radians) * radius;
    const y = -Math.cos(radians) * radius;
    return { x, y };
  };

  // Distance from center in pixels (responsive)
  useEffect(() => {
    const updateRadius = () => {
      const width = window.innerWidth;
      if (width < 480) {
        setRadius(180);
        return;
      }
      if (width < 640) {
        setRadius(210);
        return;
      }
      if (width < 768) {
        setRadius(240);
        return;
      }
      setRadius(280);
    };

    updateRadius();
    window.addEventListener('resize', updateRadius);
    return () => window.removeEventListener('resize', updateRadius);
  }, []);

  // Generate nodes from clients data
  const nodes = clients.map((client, index) => ({
    id: client.key,
    client: client,
    position: (index * 360) / clients.length, // Distribute evenly in a circle
  }));

  useEffect(() => {
    if (loading || nodes.length === 0) return;

    const isMobile = window.innerWidth < 768;
    
    const ctx = gsap.context(() => {
      // Set initial states first to prevent flashing
      if (titleRef.current) gsap.set(titleRef.current, { opacity: 1, y: 0, scale: 1 });
      
      // Title animation
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: isMobile ? 20 : 50, scale: isMobile ? 1 : 0.95 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: isMobile ? 0.6 : 1,
          ease: isMobile ? 'power2.out' : 'expo.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 85%',
            once: true, // Only animate once - prevents flashing
            toggleActions: 'play none none none', // Never reverse
          },
        }
      );

      // Nodes animation - enabled on mobile with simpler settings
      const nodeElements = nodesContainerRef.current?.querySelectorAll('.vectoral-node');
      if (nodeElements) {
        // Set initial state first
        gsap.set(nodeElements, { opacity: 1, scale: 1 });
        
        gsap.fromTo(
          nodeElements,
          { 
            opacity: 0, 
            scale: isMobile ? 0.8 : 0,
          },
          {
            opacity: 1,
            scale: 1,
            duration: isMobile ? 0.4 : 0.6,
            stagger: isMobile ? 0.03 : 0.05,
            ease: isMobile ? 'power2.out' : 'back.out(1.7)',
            scrollTrigger: {
              trigger: nodesContainerRef.current,
              start: 'top 80%',
              once: true, // Only animate once - prevents flashing
              toggleActions: 'play none none none', // Never reverse
            },
          }
        );
      }

      // Connection lines animation - enabled on mobile
      const lines = nodesContainerRef.current?.querySelectorAll('.connection-line');
      if (lines) {
        gsap.fromTo(
          lines,
          {
            scaleX: 0,
            opacity: 0,
          },
          {
            scaleX: 1,
            opacity: 0.3,
            duration: isMobile ? 0.5 : 0.8,
            stagger: isMobile ? 0.02 : 0.03,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: nodesContainerRef.current,
              start: 'top 75%',
              once: true, // Only animate once - prevents flashing
              toggleActions: 'play none none none', // Never reverse
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [loading, nodes.length]);

  const handleNodeClick = (client: Client) => {
    setSelectedClient(client);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedClient(null);
  };


  return (
    <section
      id="vectoral"
      ref={sectionRef}
      className="relative py-32 bg-white overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-kmg-accent/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div ref={titleRef} className="text-center mb-20">
          <span className="inline-block px-4 py-2 text-sm font-medium text-kmg-accent bg-kmg-accent/10 rounded-full mb-6">
            Core Infrastructure
          </span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            KMG <span className="text-gradient">Vectoral</span> System
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            The infrastructure that connects everything. Click on the client logos to explore where it's being used.
          </p>
        </div>

        {/* Circular Node Layout */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[600px]">
            <div className="text-slate-400">Loading clients...</div>
          </div>
        ) : (
          <div 
            ref={nodesContainerRef}
            className="relative flex items-center justify-center min-h-[480px] sm:min-h-[600px] py-14 sm:py-20"
          >
          {/* Center Title - Logo-like treatment */}
          <div className="absolute z-20 text-center">
            <div className="vectoral-logo relative inline-block px-10 py-8 bg-white rounded-3xl shadow-xl border-2 border-kmg-primary/20 transition-all duration-300 hover:shadow-2xl hover:border-kmg-primary/40">
              {/* Logo content */}
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl sm:text-4xl font-extrabold text-kmg-primary tracking-tighter" style={{ letterSpacing: '-0.02em' }}>KMG</span>
                  <span className="text-2xl sm:text-3xl font-extrabold text-gradient tracking-tighter" style={{ letterSpacing: '-0.02em' }}>Vectoral</span>
                </div>
                <span className="text-xs sm:text-sm font-bold text-slate-700 uppercase tracking-widest mt-1">System</span>
                <div className="w-20 h-1 bg-gradient-to-r from-transparent via-kmg-primary to-transparent mt-2 rounded-full"></div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-4">Click logos to explore</p>
          </div>

            {/* Connection lines */}
            {nodes.map((node) => {
              const angle = node.position;
              const length = radius;
              const color = '#2563eb';
              
              return (
                <div
                  key={`line-${node.id}`}
                  className="connection-line absolute pointer-events-none"
                  style={{
                    left: '50%',
                    top: '50%',
                    width: `${length}px`,
                    height: '1px',
                    transformOrigin: '0 50%',
                    transform: `rotate(${angle}deg)`,
                    opacity: hoveredNode === node.id ? 0.6 : 0.25,
                    transition: 'opacity 0.3s ease',
                  }}
                >
                  <div 
                    className="h-full w-full"
                    style={{
                      background: `repeating-linear-gradient(to right, ${color} 0px, ${color} 4px, transparent 4px, transparent 8px)`,
                    }}
                  />
                </div>
              );
            })}

            {/* Nodes */}
            {nodes.map((node) => {
              const pos = getNodePosition(node.position, radius);
              const isHovered = hoveredNode === node.id;
              
              return (
                <div
                  key={node.id}
                  className="vectoral-node absolute z-10"
                  style={{
                    left: `calc(50% + ${pos.x}px)`,
                    top: `calc(50% + ${pos.y}px)`,
                    transform: 'translate(-50%, -50%)',
                  }}
                  onMouseEnter={() => setHoveredNode(node.id)}
                  onMouseLeave={() => setHoveredNode(null)}
                >
                  <button
                    onClick={() => handleNodeClick(node.client)}
                    className={`
                      relative group
                      w-20 h-20 sm:w-24 sm:h-24
                      rounded-full
                      flex items-center justify-center
                      transition-all duration-300 ease-out
                      bg-white border-2 border-slate-200
                      cursor-pointer 
                      hover:scale-110 
                      hover:shadow-glow-lg 
                      hover:border-kmg-primary
                      active:scale-95
                      ${isHovered ? 'ring-4 ring-kmg-primary/30 scale-110 border-kmg-primary' : ''}
                      overflow-hidden
                    `}
                    aria-label={`View ${node.client.title}`}
                  >
                    {/* Client Logo */}
                    <img
                      src={`/clients/${node.client.image}`}
                      alt={node.client.title}
                      className="w-full h-full object-contain p-3"
                      loading="lazy"
                    />

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-kmg-primary/0 group-hover:bg-kmg-primary/10 transition-colors duration-300 rounded-full" />

                    {/* Hover tooltip */}
                    {isHovered && (
                      <div className={`
                        absolute left-1/2 -translate-x-1/2
                        px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap
                        bg-slate-900 text-white shadow-lg
                        pointer-events-none
                        transition-all duration-200
                        scale-95 group-hover:scale-100
                        ${node.position >= 0 && node.position < 180 ? 'bottom-full mb-2' : 'top-full mt-2'}
                        max-w-[200px] text-center
                      `}>
                        <div className="font-semibold">{node.client.title}</div>
                        <div className="text-xs opacity-80 mt-0.5">{node.client.role}</div>
                        <div 
                          className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 ${
                            node.position >= 0 && node.position < 180 
                              ? 'top-full border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900' 
                              : 'bottom-full border-l-4 border-r-4 border-b-4 border-transparent border-b-slate-900'
                          }`} 
                        />
                      </div>
                    )}

                    {/* Pulse ring on hover */}
                    <div className="absolute inset-0 rounded-full border-2 border-kmg-primary opacity-0 group-hover:opacity-100 group-hover:animate-ping" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Description */}
        <div className="mt-20 text-center max-w-3xl mx-auto">
          <p className="text-slate-600 leading-relaxed">
            The KMG Vectoral System powers integrations across industries. Each node represents 
            a real-world implementation, connecting businesses to their data, systems, and workflows.
          </p>
        </div>
      </div>

      <ClientModal
        client={selectedClient}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </section>
  );
};

export default Vectoral;
